/** Compact, source-linked test proposals; source counts are reasons, never the simulated audience. */
export const insightOpportunityCopy = {
    title: (field) => `${field}: compare versions`,
    why: 'Why this test',
    basis: (customers, buyers) => `${customers.toLocaleString('en-AU')} of ${buyers.toLocaleString('en-AU')} buyers bought it again.`,
    currentFinding: (customers, buyers) => `Current finding: ${customers.toLocaleString('en-AU')} of ${buyers.toLocaleString('en-AU')} buyers bought it again.`,
    preparedEvidence: (customers, buyers, syncedAt) => {
        const date = new Date(syncedAt);
        const label = Number.isNaN(date.valueOf())
            ? syncedAt.slice(0, 10)
            : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
        return `Prepared from ${customers.toLocaleString('en-AU')} of ${buyers.toLocaleString('en-AU')} buyers bought it again (${label}).`;
    },
    audience: 'Test audience',
    audienceSummary: (label) => `${label} (simulated)`,
    caveat: 'Uses simulated shoppers. Results are directional, not a sales prediction.',
    draftConfirmation: 'Opens your saved draft, or starts an editable one. Review the details before running. This does not run a test or change your product.',
    current: (field) => `${field} when prepared`,
    alternatives: 'New versions',
    researchTitle: 'Research behind this idea',
    researchOriginal: 'Original suggested comparison',
    researchNewer: 'Some sources have a newer reviewed interpretation. This idea keeps the version used when it was prepared.',
    researchNotRequested: 'This idea was prepared without external research.',
    researchNoMatch: 'No matching evidence was returned from the curated research library. This does not mean no research exists.',
    researchUnused: 'Research was considered, but no source was cited for this idea.',
    researchCaveat: 'External research informs a test idea, not a proven result for your store. Later edits are not automatically supported by these sources.',
    researchRestricted: 'The original research is no longer available for use. You can still reopen and edit a saved draft.',
    researchUse: (target, relation) => `${target}: ${relation}`,
    researchField: 'Field choice',
    researchAlternative: (id) => `Version ${id}`,
    researchRelations: { supports: 'informed by', qualifies: 'limited by', challenges: 'questioned by' },
    researchInterpretation: 'Research interpretation',
    researchLimitations: 'Limits of this research',
    researchQuote: 'Source passage',
    details: 'Test details',
    close: 'Close',
    comparisonLabel: (product) => `Comparison for ${product}`,
    regionLabel: (product) => `Test idea for ${product}`,
    test: 'Test this',
    loading: 'Loading prepared test ideas…',
    readFailed: 'Test ideas could not be loaded. Refresh Insights to try again.',
    refresh: 'Refresh Insights to check these test ideas against the latest information.',
    noLongerListed: 'This comparison is no longer listed. Test this can still reopen a draft you already saved from it.',
    fieldChanged: 'The product field no longer matches this comparison or cannot be tested. Choose another comparison, or create a new test from Tests.',
    sourceUnavailable: 'This comparison is no longer available for a new draft. Open any saved test from Tests, or choose another comparison.',
    productUnavailable: 'This product is no longer for sale. Choose another product to create a test.',
    seedFailed: 'The draft could not be opened. Try again, or refresh Insights if the product has changed.',
    scopeChanged: 'The store changed. Refresh Insights before choosing a test.',
    // Ladle-only exploration. These labels do not enable a merchant feature.
    readingPreview: {
        heading: 'Research worth a look',
        notice: 'Design preview · read-only',
        intro: 'Examples from published research, not findings about your store. Nothing here changes a product or starts a test.',
        takeaway: 'A · At a glance',
        question: 'B · Start with a question',
        reader: 'C · Browse by topic',
        takeawayDescription: 'Three static insights, shown with an existing Insights card for context. Customer counts below are sample data.',
        staticHeading: 'Worth a look',
        staticIntro: 'Ideas from other businesses and research—not findings about your store.',
        empty: 'No examples to show right now.',
        restricted: 'These examples need a fresh look before we show them again.',
        failed: 'We couldn’t load your examples. Your store insights are still available.',
        pending: 'We’re finding examples that could be useful for your store.',
        insufficientContext: 'We need a little more about your store before choosing useful examples.',
        deferred: 'New examples are paused for now. Your store insights are still available.',
        dismiss: 'Hide this example',
        retry: 'Try again',
        worthAsking: 'Worth asking',
        editorial: 'At a glance · Editorial question',
        aboutInsight: (title) => `About this insight: ${title}`,
        questionDescription: 'One question, what we can learn from an example, and what it leaves unanswered.',
        readerDescription: 'Choose a topic to inspect an example and the research behind it.',
        reflection: 'A question to consider',
        finding: 'What the research found',
        unknown: 'What this does not tell us',
        context: 'About this example',
        participants: 'Who was involved',
        comparison: 'What was compared',
        reported: 'Reported result',
        topics: 'Topics',
        source: 'Read the source',
        topicsLabel: 'Choose a research topic',
        selectedTopic: (topic) => `${topic}, selected`,
    },
};
//# sourceMappingURL=insight-opportunities.js.map